import React, { ChangeEventHandler, Dispatch, SetStateAction } from 'react';
import {
  CheckIcon,
  XMarkIcon,
  DocumentPlusIcon,
} from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import { classNames } from '@geerd-nx/utils';
export interface BaseAutoCompleteProps<T> {
  setSearch: Dispatch<SetStateAction<string>>;
  setValue: (value: T) => void;
  create?: (value: string) => void;
  search: string;
  placeholder?: string;
  accessor: keyof T;
  readonly?: boolean;
  isLoading?: boolean;
  data: T[];
  onClear?: () => void;
}
export function AutoComplete<T>({
  data,
  placeholder,
  isLoading,
  readonly = false,
  setSearch,
  setValue,
  accessor,
  create,
  onClear,
  search,
}: BaseAutoCompleteProps<T>) {
  const changeHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value);
  };
  const clearHanlder = () => {
    setSearch('');
  };

  const debouncedChangeHandler = debounce(changeHandler, 300);
  const onChnageComboBox = (value: T) => {
    if (readonly) return;

    setSearch(
      typeof (value[accessor] as any) === 'string'
        ? (value[accessor] as string)
        : placeholder || ''
    );
  };
  return (
    <Combobox onChange={onChnageComboBox}>
      <div className="relative mt-1">
        <Combobox.Input
          readOnly={readonly}
          placeholder={search || placeholder}
          autoComplete="off"
          className={`w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm  placeholder:opacity-100 placeholder:text-gray-900 text-gray-900 placeholder:text-opacity-100 ${
            readonly
              ? 'bg-gray-100 focus:ring-0 focus:outline-none focus:border-gray-300'
              : 'focus:border-primary-500 focus:outline-none bg-white focus:ring-1 focus:ring-primary-500'
          } sm:text-sm`}
          onChange={debouncedChangeHandler}
        />
        {!readonly ? (
          <Combobox.Button
            onClick={() => {
              clearHanlder();
              if (onClear) {
                onClear();
              }
            }}
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
        ) : null}
        {search && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white  text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {isLoading ? (
              <Combobox.Option
                className="relative select-none py-2 pl-3 pr-9 text-gray-900"
                value={null}
                disabled={true}
              >
                <div className="w-4 h-4 box-border border-2 border-solid rounded-full border-neutral-200 border-r-neutral-400 m-auto animate-spin" />
              </Combobox.Option>
            ) : data.length ? (
              React.Children.toArray(
                data.map((item, index) => (
                  <>
                    <Combobox.Option
                      value={item}
                      onClick={() => {
                        setValue(item);
                      }}
                      className={({ active }) =>
                        classNames(
                          'relative cursor-pointer select-none py-2 pl-3 pr-9',
                          active ? 'bg-primary-600 text-white' : 'text-gray-900'
                        )
                      }
                    >
                      {({ active, selected }) => (
                        <>
                          <span
                            className={classNames(
                              'block truncate',
                              selected ? 'font-semibold' : ''
                            )}
                          >
                            {item[accessor] as string}
                          </span>
                          {active && (
                            <span
                              className={classNames(
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                active ? 'text-white' : 'text-primary-600'
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                      {/* {({
                        active,
                        selected,
                      }: {
                        active: boolean;
                        selected: T;
                      }) => (

                      )} */}
                    </Combobox.Option>
                    {index === data.length - 1 &&
                      create &&
                      !find(data, function (obj) {
                        if (obj[accessor] === search) {
                          return true;
                        }
                        return undefined;
                      }) &&
                      !!search?.trim() && (
                        <Combobox.Option
                          value={search}
                          onClick={() => {
                            create(search);
                          }}
                          className={({ active }) =>
                            classNames(
                              'relative  select-none py-2 pl-3 pr-9 cursor-pointer',
                              active
                                ? 'bg-primary-600 text-white'
                                : 'text-gray-900'
                            )
                          }
                        >
                          {({ active, selected }) => (
                            <>
                              <span
                                className={classNames(
                                  'absolute inset-y-0 left-0 flex items-center pl-4',
                                  active ? 'text-white' : 'text-primary-600'
                                )}
                              >
                                <DocumentPlusIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                              <span
                                className={classNames(
                                  'block truncate ml-8',
                                  selected ? 'font-semibold' : ''
                                )}
                              >
                                Create : {search?.trim()}
                              </span>
                            </>
                          )}
                        </Combobox.Option>
                      )}
                  </>
                ))
              )
            ) : create && search?.trim() ? (
              <Combobox.Option
                value={search}
                onClick={() => {
                  create(search);
                }}
                className={({ active }) =>
                  classNames(
                    'relative  select-none py-2 pl-3 pr-9 cursor-pointer',
                    active ? 'bg-primary-600 text-white' : 'text-gray-900'
                  )
                }
              >
                {({ active, selected }) => (
                  <>
                    <span
                      className={classNames(
                        'absolute inset-y-0 left-0 flex items-center pl-4',
                        active ? 'text-white' : 'text-primary-600'
                      )}
                    >
                      <DocumentPlusIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </span>
                    <span
                      className={classNames(
                        'block truncate ml-8',
                        selected ? 'font-semibold' : ''
                      )}
                    >
                      Create : {search?.trim()}
                    </span>
                  </>
                )}
              </Combobox.Option>
            ) : search ? (
              <Combobox.Option
                className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900"
                value={null}
                disabled={true}
              >
                No result found
              </Combobox.Option>
            ) : null}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}
